import React, { useState, useEffect } from 'react';
import { workAssignmentAPI, holidayAPI, leaveAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BranchSelector from './BranchSelector';
import WorkAssignmentForm from './WorkAssignmentForm';
import LeaveManagementForm from './LeaveManagementForm';
import BulkAssignmentForm from './BulkAssignmentForm';
import DirectAssignForm from './DirectAssignForm';
import { FiBriefcase, FiShield, FiCalendar, FiActivity, FiTrash } from './Icons';
import styled from 'styled-components';

const CalendarContainer = styled.div`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  font-family: ${props => props.theme?.fonts?.main || "'Sarabun', sans-serif"};
  box-sizing: border-box;
  max-width: none;
  margin: 0;
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.xs};
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
  background: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.card};
  border: 1px solid ${props => props.theme.colors.border.light};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
    margin-bottom: ${props => props.theme.spacing.lg};
    padding: ${props => props.theme.spacing.md};
    
    h2 {
      margin: 0;
      font-size: 1.5rem;
      text-align: center;
    }
  }
  
  @media (max-width: 480px) {
    h2 {
      font-size: 1.3rem;
    }
  }
`;

const RoleLegend = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
  align-items: center;
  margin-top: ${props => props.theme.spacing.md};
  
  @media (max-width: 768px) {
    justify-content: center;
    margin-top: ${props => props.theme.spacing.sm};
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.secondary};
  
  .badge {
    width: 16px;
    height: 16px;
    border-radius: ${props => props.theme.borderRadius.small};
    display: inline-block;
  }
`;

const HeaderContent = styled.div`
  width: 100%;
  
  .header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: ${props => props.theme.spacing.lg};
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: ${props => props.theme.spacing.md};
    }
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.lg};
  width: 100%;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(5, 1fr);
    gap: ${props => props.theme.spacing.sm};
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
    gap: ${props => props.theme.spacing.sm};
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${props => props.theme.spacing.xs};
    margin-top: ${props => props.theme.spacing.md};
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${props => props.theme.spacing.xs};
  }
  
  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const CalendarDay = styled.div`
  background: ${props => props.theme.colors.surface};
  min-height: 200px;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.large};
  position: relative;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.theme.shadows.light};
  overflow: hidden;
  
  /* Direct assign mode selected date styling */
  ${props => {
    if (props.isDirectAssignSelected) {
      return `
        background: ${props.theme.colors.primarySoft} !important;
        border-color: ${props.theme.colors.primary} !important;
        border-width: 2px;
        box-shadow: 0 4px 12px rgba(147, 12, 0, 0.15) !important;

        &::before {
          transform: scaleX(1);
          background: ${props.theme.colors.primary};
        }
      `;
    }
    return '';
  }}

  /* Staffing requirement background colors */
  ${props => {
    if (props.isDirectAssignSelected) return ''; // Don't apply staffing colors if direct assign selected
    
    if (props.staffingStatus === 'insufficient') {
      return `
        background: #fdf3f1;
        border-color: #e7c9c3;
      `;
    } else if (props.staffingStatus === 'adequate') {
      return `
        background: #eef5f0;
        border-color: #cfe3d7;
      `;
    }
    return '';
  }}
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.medium};
    border-color: ${props => props.theme.colors.primary};
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  &.today {
    background: linear-gradient(135deg, ${props => props.theme.colors.surface} 0%, ${props => props.theme.colors.secondary} 100%);
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => props.theme.shadows.card};
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  @media (max-width: 768px) {
    min-height: 180px;
    padding: ${props => props.theme.spacing.sm};
  }
  
  @media (max-width: 480px) {
    min-height: 160px;
    padding: ${props => props.theme.spacing.sm};
  }
`;

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-weight: 500;
  font-size: 14px;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.primary};
  border-bottom: 2px solid ${props => props.theme.colors.border.light};
  padding-bottom: ${props => props.theme.spacing.xs};
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  
  .date-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
  
  .role-summary {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    
    .shift-summary {
      display: flex;
      gap: 3px;
      flex-wrap: wrap;
      align-items: center;
      
      .shift-label {
        font-size: 9px;
        font-weight: 700;
        color: ${props => props.theme.colors.text.secondary};
        min-width: 20px;
        text-transform: uppercase;
      }
      
      .role-count {
        display: inline-flex;
        align-items: center;
        background: ${props => props.theme.colors.surface};
        border: 1px solid ${props => props.theme.colors.border.light};
        border-radius: 10px;
        padding: 1px 4px;
        font-size: 9px;
        font-weight: 600;
        color: white;
        min-width: 14px;
        height: 14px;
        justify-content: center;
        
        &.manager { background: #e91e63; }
        &.makeup { background: #9c27b0; }
        &.photographer { background: #2196f3; }
        &.retoucher { background: #4caf50; }
        
        &.zero {
          background: #f5f5f5;
          color: #999;
          border-color: #e7e3db;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    margin-bottom: ${props => props.theme.spacing.xs};
    
    .role-summary .role-count {
      font-size: 9px;
      padding: 1px 4px;
    }
  }
`;

const DayNumber = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const DayOfWeek = styled.span`
  font-size: 12px;
  color: ${props => props.theme.colors.text.secondary};
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const Assignment = styled.div`
  background: ${props => {
    // Cross-branch assignments (magenta/purple gradient)
    if (props.isCrossBranch) {
      return 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)';
    }
    // Current branch assignments
    switch (props.shift) {
      case 'day': return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)';
      case 'noon': return 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 100%)';
      default: return 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)';
    }
  }};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.xs};
  margin: ${props => props.theme.spacing.xs} 0;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  border-left: 4px solid ${props => {
    // Cross-branch assignments (magenta border)
    if (props.isCrossBranch) {
      return '#930c00';
    }
    // Current branch assignments
    switch (props.shift) {
      case 'day': return '#1976d2';
      case 'noon': return '#f57c00';
      default: return '#757575';
    }
  }};
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 20px;
    height: 100%;
    background: ${props => {
      // Cross-branch assignments (magenta gradient)
      if (props.isCrossBranch) {
        return 'linear-gradient(90deg, transparent, rgba(194, 24, 91, 0.3))';
      }
      // Current branch assignments
      switch (props.shift) {
        case 'day': return 'linear-gradient(90deg, transparent, rgba(25, 118, 210, 0.2))';
        case 'noon': return 'linear-gradient(90deg, transparent, rgba(245, 124, 0, 0.2))';
        default: return 'linear-gradient(90deg, transparent, rgba(117, 117, 117, 0.2))';
      }
    }};
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    border-left-width: 5px;
  }
  
  .employee-name {
    font-weight: 600;
    color: ${props => {
      // Cross-branch assignments (magenta text)
      if (props.isCrossBranch) {
        return '#ad1457';
      }
      // Current branch assignments
      switch (props.shift) {
        case 'day': return '#1565c0';
        case 'noon': return '#e65100';
        default: return '#424242';
      }
    }};
    font-size: 0.8rem;
    line-height: 1.2;
    margin-bottom: 2px;
  }
  
  .employee-role {
    font-size: 0.65rem;
    color: ${props => {
      // Cross-branch assignments (magenta text)
      if (props.isCrossBranch) {
        return '#930c00';
      }
      // Current branch assignments
      switch (props.shift) {
        case 'day': return '#1976d2';
        case 'noon': return '#f57c00';
        default: return '#757575';
      }
    }};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
    opacity: 0.8;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .shift-badge {
    display: inline-block;
    background: ${props => {
      // Cross-branch assignments (magenta badge)
      if (props.isCrossBranch) {
        return '#930c00';
      }
      // Current branch assignments
      switch (props.shift) {
        case 'day': return '#1976d2';
        case 'noon': return '#f57c00';
        default: return '#757575';
      }
    }};
    color: white;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 0.6rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.2);
  }
  
  .role-badge {
    display: inline-block;
    background: ${props => {
      switch (props.role) {
        case 'store_manager': return '#e91e63';
        case 'manager_assistant': return '#e91e63';
        case 'makeup': return '#9c27b0';
        case 'makeup_supervisor': return '#9c27b0';
        case 'photographer': return '#2196f3';
        case 'photographer_supervisor': return '#2196f3';
        case 'retoucher': return '#4caf50';
        case 'retoucher_supervisor': return '#4caf50';
        default: return '#757575';
      }
    }};
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.6rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-left: 4px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.3);
  }
`;

const Holiday = styled.div`
  background-color: ${props => {
    switch (props.group) {
      case 'A': return '#ffebee';
      case 'B': return '#e8f5e8';
      default: return '#fff3e0';
    }
  }};
  color: ${props => {
    switch (props.group) {
      case 'A': return '#c62828';
      case 'B': return '#2e7d32';
      default: return '#ef6c00';
    }
  }};
  padding: 2px 4px;
  margin: 2px 0;
  border-radius: 3px;
  font-size: 10px;
  font-weight: bold;
  border-left: 3px solid ${props => {
    switch (props.group) {
      case 'A': return '#f44336';
      case 'B': return '#4caf50';
      default: return '#ff9800';
    }
  }};
  text-align: center;
  display: block;
  clear: both;
`;

const LeaveItem = styled.div`
  background-color: ${props => {
    switch (props.leaveType) {
      case 'Business leave': return '#e3f2fd';
      case 'Sick leave': return '#fff3e0';
      case 'Vacation leave': return '#e8f5e8';
      case 'Month leave': return '#f3e5f5';
      case 'Extra day leave': return '#fce4ec';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.leaveType) {
      case 'Business leave': return '#1976d2';
      case 'Sick leave': return '#f57c00';
      case 'Vacation leave': return '#388e3c';
      case 'Month leave': return '#7b1fa2';
      case 'Extra day leave': return '#930c00';
      default: return '#616161';
    }
  }};
  padding: 4px 6px;
  margin: 2px 0;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  border-left: 3px solid ${props => {
    switch (props.leaveType) {
      case 'Business leave': return '#2196f3';
      case 'Sick leave': return '#ff9800';
      case 'Vacation leave': return '#4caf50';
      case 'Month leave': return '#9c27b0';
      case 'Extra day leave': return '#e91e63';
      default: return '#9e9e9e';
    }
  }};
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const Button = styled.button`
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.text.inverse};
  border: none;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.medium};
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.theme.shadows.light};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.medium};
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &.danger {
    background: ${props => props.theme.colors.status.error};
    
    &:hover {
      background: #d32f2f;
    }
  }
  
  &.secondary {
    background: ${props => props.theme.colors.border.medium};
    color: ${props => props.theme.colors.text.primary};
    
    &:hover {
      background: ${props => props.theme.colors.border.dark};
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      transform: none;
      box-shadow: ${props => props.theme.shadows.light};
    }
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    font-size: 12px;
  }
  
  @media (max-width: 480px) {
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    font-size: 11px;
  }
`;

const ShiftSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.sm};
  
  @media (max-width: 480px) {
    margin-bottom: ${props => props.theme.spacing.xs};
  }
`;

const ShiftLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary}, ${props => props.theme.colors.secondary}80);
  border-radius: ${props => props.theme.borderRadius.small};
  border-left: 3px solid ${props => props.theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  
  @media (max-width: 480px) {
    font-size: 0.65rem;
    padding: ${props => props.theme.spacing.xs};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(147, 12, 0, 0.15);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.large};
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  box-shadow: ${props => props.theme.shadows.heavy};
  border: 1px solid ${props => props.theme.colors.border.light};
  animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideIn {
    from { 
      transform: translateY(-20px);
      opacity: 0;
    }
    to { 
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: 95vh;
    border-radius: ${props => props.theme.borderRadius.medium};
  }
  
  @media (max-width: 480px) {
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
  }
`;

const DeleteConfirmation = styled.div`
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  min-width: 400px;
  
  @media (max-width: 480px) {
    min-width: auto;
    padding: ${props => props.theme.spacing.lg};
  }
  
  h3 {
    color: ${props => props.theme.colors.status.error};
    margin-bottom: ${props => props.theme.spacing.lg};
    font-size: 1.2rem;
  }
  
  .assignment-info {
    background: ${props => props.theme.colors.background};
    padding: ${props => props.theme.spacing.md};
    border-radius: ${props => props.theme.borderRadius.medium};
    margin: ${props => props.theme.spacing.lg} 0;
    border-left: 4px solid ${props => props.theme.colors.status.error};
    
    .employee-name {
      font-weight: 600;
      font-size: 1.1rem;
      color: ${props => props.theme.colors.text.primary};
      margin-bottom: ${props => props.theme.spacing.xs};
    }
    
    .assignment-details {
      font-size: 0.9rem;
      color: ${props => props.theme.colors.text.secondary};
    }
  }
  
  .button-group {
    display: flex;
    gap: ${props => props.theme.spacing.md};
    justify-content: center;
    margin-top: ${props => props.theme.spacing.lg};
    
    @media (max-width: 480px) {
      flex-direction: column;
    }
  }
`;

const StaffingRequirements = styled.div`
  background: #fbf4e6;
  border: 1px solid #e6dcc4;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.md};
  margin: ${props => props.theme.spacing.md} 0;
  text-align: center;
  box-shadow: ${props => props.theme.shadows.light};
  
  .requirements-title {
    font-weight: 600;
    color: #6b645c;
    margin-bottom: ${props => props.theme.spacing.xs};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${props => props.theme.spacing.xs};
  }
  
  .requirements-text {
    font-size: 0.875rem;
    color: #6b645c;
    
    .legend {
      display: flex;
      justify-content: center;
      gap: ${props => props.theme.spacing.lg};
      margin-top: ${props => props.theme.spacing.sm};
      flex-wrap: wrap;
      
      .legend-item {
        display: flex;
        align-items: center;
        gap: ${props => props.theme.spacing.xs};
        font-size: 0.75rem;
        
        .color-box {
          width: 16px;
          height: 16px;
          border-radius: 3px;
          border: 1px solid #e7e3db;
        }
      }
    }
  }
`;

const EmployeeSummary = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.surface} 0%, ${props => props.theme.colors.secondary}20 100%);
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: ${props => props.theme.spacing.lg};
  margin: ${props => props.theme.spacing.lg} 0;
  box-shadow: ${props => props.theme.shadows.card};
  
  .summary-header {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.sm};
    margin-bottom: ${props => props.theme.spacing.md};
    font-size: 1.1rem;
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    border-bottom: 2px solid ${props => props.theme.colors.border.light};
    padding-bottom: ${props => props.theme.spacing.sm};
  }
  
  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: ${props => props.theme.spacing.md};
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
  
  .summary-card {
    background: ${props => props.theme.colors.surface};
    border: 1px solid ${props => props.theme.colors.border.light};
    border-radius: ${props => props.theme.borderRadius.medium};
    padding: ${props => props.theme.spacing.md};
    text-align: center;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${props => props.theme.shadows.medium};
    }
    
    .card-icon {
      font-size: 2rem;
      margin-bottom: ${props => props.theme.spacing.xs};
    }
    
    .card-title {
      font-size: 0.875rem;
      font-weight: 600;
      color: ${props => props.theme.colors.text.secondary};
      margin-bottom: ${props => props.theme.spacing.xs};
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .card-value {
      font-size: 1.5rem;
      font-weight: 700;
      color: ${props => props.theme.colors.primary};
      margin-bottom: ${props => props.theme.spacing.xs};
    }
    
    .card-detail {
      font-size: 0.75rem;
      color: ${props => props.theme.colors.text.secondary};
      line-height: 1.4;
      text-align: left;
      
      .branch-breakdown {
        margin-top: ${props => props.theme.spacing.xs};
        
        .total-line {
          font-weight: 600;
          color: ${props => props.theme.colors.text.primary};
          margin-bottom: ${props => props.theme.spacing.xs};
          padding-bottom: ${props => props.theme.spacing.xs};
          border-bottom: 1px solid ${props => props.theme.colors.border.light};
        }
        
        .branch-line {
          margin: 2px 0;
          padding-left: ${props => props.theme.spacing.sm};
          position: relative;
          
          &::before {
            content: '•';
            position: absolute;
            left: 0;
            color: ${props => props.theme.colors.primary};
            font-weight: bold;
          }
          
          .branch-name {
            font-weight: 500;
            color: ${props => props.theme.colors.text.primary};
          }
        }
      }
      
      .leave-breakdown {
        margin-top: ${props => props.theme.spacing.xs};
        
        .total-line {
          font-weight: 600;
          color: ${props => props.theme.colors.text.primary};
          margin-bottom: ${props => props.theme.spacing.xs};
          padding-bottom: ${props => props.theme.spacing.xs};
          border-bottom: 1px solid ${props => props.theme.colors.border.light};
        }
        
        .leave-type-line {
          margin: 2px 0;
          
          .leave-type-name {
            font-weight: 500;
            color: ${props => props.theme.colors.text.primary};
          }
        }
      }
    }
    
    &.work-days {
      border-left: 4px solid #2196f3;
      .card-icon { color: #2196f3; }
      .card-value { color: #2196f3; }
    }
    
    &.leave-days {
      border-left: 4px solid #ff9800;
      .card-icon { color: #ff9800; }
      .card-value { color: #ff9800; }
    }
    
    &.total-days {
      border-left: 4px solid #4caf50;
      .card-icon { color: #4caf50; }
      .card-value { color: #4caf50; }
    }
  }
  
  .date-range {
    margin-top: ${props => props.theme.spacing.md};
    padding: ${props => props.theme.spacing.sm};
    background: ${props => props.theme.colors.background};
    border-radius: ${props => props.theme.borderRadius.small};
    font-size: 0.875rem;
    color: ${props => props.theme.colors.text.secondary};
    text-align: center;
    border: 1px solid ${props => props.theme.colors.border.light};
  }
`;

const BranchCalendar = ({ defaultBranchId = null, hideHeader = false }) => {
  const [selectedBranchId, setSelectedBranchId] = useState(defaultBranchId);
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    // Always start with the first day of the current month
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDayOfMonth;
  });
  const [assignments, setAssignments] = useState([]);
  const [crossBranchAssignments, setCrossBranchAssignments] = useState([]); // Assignments from other branches
  const [holidays, setHolidays] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [viewMode, setViewMode] = useState('assignments'); // 'assignments' or 'leave'
  const [showBulkAssignmentForm, setShowBulkAssignmentForm] = useState(false);
  const [filterRole, setFilterRole] = useState(''); // Filter by role
  const [filterName, setFilterName] = useState(''); // Filter by employee name

  // Map simplified role categories to actual roles (base + supervisor)
  const getRolesForCategory = (category) => {
    const roleMap = {
      'manager': ['store_manager', 'manager_assistant'],
      'makeup': ['makeup', 'makeup_supervisor'],
      'photographer': ['photographer', 'photographer_supervisor'],
      'retoucher': ['retoucher', 'retoucher_supervisor']
    };
    return roleMap[category] || [category];
  };
  
  // Direct assignment states
  const [isDirectAssignMode, setIsDirectAssignMode] = useState(false);
  const [directAssignSelectedDates, setDirectAssignSelectedDates] = useState([]);
  
  const { accessibleBranches, hasPermission, homeBranchId } = useAuth();

  // Helper function to create timezone-safe date string
  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    // If defaultBranchId is provided, use it and don't auto-select
    if (defaultBranchId) {
      setSelectedBranchId(defaultBranchId);
      return;
    }
    
    // Auto-select home branch first, then fall back to first accessible branch
    if (!selectedBranchId) {
      if (homeBranchId && (accessibleBranches === 'ALL_BRANCHES' || accessibleBranches.includes(homeBranchId))) {
        // User has a home branch and can access it
        setSelectedBranchId(homeBranchId);
      } else if (Array.isArray(accessibleBranches) && accessibleBranches.length > 0) {
        // Fall back to first accessible branch
        setSelectedBranchId(accessibleBranches[0]);
      }
    }
  }, [accessibleBranches, homeBranchId, selectedBranchId, defaultBranchId]);

  useEffect(() => {
    fetchHolidays(); // Always fetch holidays for the current date
    fetchLeaveRequests(); // Fetch leave requests for admin view
    if (selectedBranchId) {
      fetchBranchAssignments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId, currentDate, filterName]);

  const fetchBranchAssignments = async () => {
    if (!selectedBranchId) return;
    
    setLoading(true);
    try {
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const response = await workAssignmentAPI.getBranchCalendar(
        selectedBranchId,
        getLocalDateString(startDate),
        getLocalDateString(endDate)
      );
      
      // Extract assignments from the nested structure
      if (response.data && response.data.length > 0) {
        const allAssignments = [];
        response.data[0].schedules.forEach(schedule => {
          schedule.day_shift_assignments.forEach(assignment => {
            allAssignments.push({...assignment, shift_type: 'day'});
          });
          schedule.noon_shift_assignments.forEach(assignment => {
            allAssignments.push({...assignment, shift_type: 'noon'});
          });
        });
        setAssignments(allAssignments);
      } else {
        setAssignments([]);
      }
      
      // Fetch cross-branch assignments when name filter is active
      if (filterName && filterName.trim() !== '') {
        await fetchCrossBranchAssignments();
      } else {
        setCrossBranchAssignments([]);
      }
    } catch (error) {
      console.error('Error fetching branch assignments:', error);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCrossBranchAssignments = async () => {
    if (!selectedBranchId || !filterName || filterName.trim() === '') {
      setCrossBranchAssignments([]);
      return;
    }
    
    try {
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      // Fetch ALL work assignments for the date range (not filtered by branch)
      const response = await workAssignmentAPI.getAll({
        start_date: getLocalDateString(startDate),
        end_date: getLocalDateString(endDate)
      });
      
      // Filter for:
      // 1. Assignments from OTHER branches (not current branch)
      // 2. Match the employee name filter
      const nameFilter = filterName.toLowerCase();
      const otherBranchAssignments = (response.data || []).filter(assignment => {
        const isDifferentBranch = assignment.branch_id !== selectedBranchId;
        const matchesName = assignment.user?.name?.toLowerCase().includes(nameFilter);
        return isDifferentBranch && matchesName;
      });
      
      setCrossBranchAssignments(otherBranchAssignments);
    } catch (error) {
      console.error('Error fetching cross-branch assignments:', error);
      setCrossBranchAssignments([]);
    }
  };

  const fetchHolidays = async () => {
    try {
      const year = currentDate.getFullYear();
      
      // Fetch all holidays for the current year
      const response = await holidayAPI.getAll({
        year: year
      });
      
      setHolidays(response.data || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      setHolidays([]);
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      // Scope leave to the displayed month via start_date/end_date.
      // The /leave/requests endpoint has NO year/month params (they are silently
      // ignored, which returned ALL-TIME approved leave and inflated Leave Days).
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      const response = await leaveAPI.getLeaveRequests({
        start_date: getLocalDateString(startDate),
        end_date: getLocalDateString(endDate),
        status: 'approved'
      });

      setLeaveRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
      setLeaveRequests([]);
    }
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    
    // Only include days from the current month
    for (let date = 1; date <= lastDay.getDate(); date++) {
      days.push(new Date(year, month, date));
    }
    
    return days;
  };

  const getAssignmentsForDate = (date) => {
    const dateStr = getLocalDateString(date);
    let filteredAssignments = assignments.filter(assignment => 
      assignment.assignment_date === dateStr
    );
    
    // Get cross-branch assignments for this date
    const crossBranchForDate = crossBranchAssignments.filter(assignment => 
      assignment.assignment_date === dateStr
    );
    
    // Apply role filter
    if (filterRole) {
      const allowedRoles = getRolesForCategory(filterRole);
      filteredAssignments = filteredAssignments.filter(assignment => 
        allowedRoles.includes(assignment.role)
      );
    }
    
    // Apply name filter
    if (filterName) {
      const nameFilter = filterName.toLowerCase();
      filteredAssignments = filteredAssignments.filter(assignment => 
        assignment.user?.name?.toLowerCase().includes(nameFilter)
      );
    }
    
    // Combine current branch and cross-branch assignments
    // Mark cross-branch assignments with isCrossBranch flag
    const combinedAssignments = [
      ...filteredAssignments,
      ...crossBranchForDate.map(a => ({ ...a, isCrossBranch: true }))
    ];
    
    return combinedAssignments;
  };

  const getHolidaysForDate = (date) => {
    const dateStr = getLocalDateString(date);
    return holidays.filter(holiday => 
      holiday.date === dateStr
    );
  };

  const getLeaveForDate = (date) => {
    const dateStr = getLocalDateString(date);
    return leaveRequests.filter(leave => 
      leave.leave_date === dateStr
    );
  };

  // Helper function to get role counts for a date by shift
  const getRoleCountsByShift = (assignments) => {
    const dayShiftCounts = {
      manager: 0,
      makeup: 0,
      photographer: 0,
      retoucher: 0
    };
    
    const noonShiftCounts = {
      manager: 0,
      makeup: 0,
      photographer: 0,
      retoucher: 0
    };
    
    // Map actual roles to display categories
    const roleToCategory = {
      'store_manager': 'manager',
      'manager_assistant': 'manager',
      'makeup': 'makeup',
      'makeup_supervisor': 'makeup',
      'photographer': 'photographer',
      'photographer_supervisor': 'photographer',
      'retoucher': 'retoucher',
      'retoucher_supervisor': 'retoucher'
    };
    
    assignments.forEach(assignment => {
      const role = assignment.role;
      const shift = assignment.shift_type;
      const category = roleToCategory[role];
      
      if (category) {
        if (shift === 'day') {
          dayShiftCounts[category]++;
        } else if (shift === 'noon') {
          noonShiftCounts[category]++;
        }
      }
    });
    
    return { dayShiftCounts, noonShiftCounts };
  };

  // Helper function to check staffing requirements for a date
  const checkStaffingRequirements = (assignments) => {
    // Count roles by shift
    const dayShiftCounts = {
      makeup: 0,
      photographer: 0,
      manager: 0,
      retoucher: 0
    };
    
    const noonShiftCounts = {
      makeup: 0,
      photographer: 0,
      manager: 0,
      retoucher: 0
    };
    
    // Map actual roles to display categories
    const roleToCategory = {
      'store_manager': 'manager',
      'manager_assistant': 'manager',
      'makeup': 'makeup',
      'makeup_supervisor': 'makeup',
      'photographer': 'photographer',
      'photographer_supervisor': 'photographer',
      'retoucher': 'retoucher',
      'retoucher_supervisor': 'retoucher'
    };
    
    assignments.forEach(assignment => {
      const actualRole = assignment.role;
      const category = roleToCategory[actualRole];
      const shift = assignment.shift_type;
      
      if (category) {
        if (shift === 'day') {
          dayShiftCounts[category]++;
        } else if (shift === 'noon') {
          noonShiftCounts[category]++;
        }
      }
    });
    
    // Check minimum requirements: 1 makeup, 1 photographer per shift
    const dayShiftAdequate = dayShiftCounts.makeup >= 1 && dayShiftCounts.photographer >= 1;
    const noonShiftAdequate = noonShiftCounts.makeup >= 1 && noonShiftCounts.photographer >= 1;
    
    // Both shifts must meet requirements for green status
    if (dayShiftAdequate && noonShiftAdequate) {
      return 'adequate'; // Green background
    } else {
      return 'insufficient'; // Red background
    }
  };

  // Helper function to check if employee is working on their holiday
  const getEmployeeHolidayInfo = (assignment) => {
    const user = assignment.user;
    if (!user || !user.holiday_group_id || !assignment.assignment_date) return null;
    
    // Get holidays for the assignment date
    const assignmentDate = new Date(assignment.assignment_date);
    const dayHolidays = getHolidaysForDate(assignmentDate);
    
    // Check if any holiday matches the employee's holiday group
    const matchingHoliday = dayHolidays.find(holiday => 
      holiday.holiday_group_id === user.holiday_group_id
    );
    
    return matchingHoliday;
  };

  // Helper function to format employee display name with location info and holiday tag
  const formatEmployeeDisplayName = (assignment) => {
    const user = assignment.user;
    if (!user) return 'No name';
    
    let displayName = user.name;
    
    // Check for holiday overtime
    const holidayInfo = getEmployeeHolidayInfo(assignment);
    if (holidayInfo) {
      displayName += ` 🏖️ OT`;
    }
    
    // Try multiple ways to get home branch name
    const homeBranch = user.home_branch?.name || 
                      user.home_branch_name || 
                      (user.home_branch_id && `Branch ${user.home_branch_id}`);
    
    if (user.is_moveable && homeBranch) {
      displayName += ` (${homeBranch} - Moveable)`;
    } else if (user.is_moveable) {
      displayName += ` (Moveable)`;
    } else if (homeBranch) {
      displayName += ` (${homeBranch})`;
    }
    
    return displayName;
  };

  // Function to calculate employee statistics when filtering by name
  const getEmployeeStatistics = () => {
    if (!filterName || filterName.trim().length < 2) return null;
    
    const nameFilter = filterName.toLowerCase().trim();
    
    // Merge current branch assignments and cross-branch assignments for the matching employee
    const currentBranchAssignments = assignments.filter(assignment => 
      assignment.user?.name?.toLowerCase().includes(nameFilter)
    );
    
    const otherBranchAssignments = crossBranchAssignments.filter(assignment => 
      assignment.user?.name?.toLowerCase().includes(nameFilter)
    );
    
    // Combine all assignments from all branches
    const employeeAssignments = [...currentBranchAssignments, ...otherBranchAssignments];
    
    // Filter leave requests for the current month and matching employee name
    const employeeLeave = leaveRequests.filter(leave => 
      leave.user?.name?.toLowerCase().includes(nameFilter)
    );
    
    // If no data found, return null
    if (employeeAssignments.length === 0 && employeeLeave.length === 0) {
      return null;
    }
    
    // Get unique work dates (across all branches)
    const workDates = [...new Set(employeeAssignments.map(assignment => assignment.assignment_date))];
    
    // Get unique leave dates
    const leaveDates = [...new Set(employeeLeave.map(leave => leave.leave_date))];
    
    // Get employee name (use the first match for display)
    const employeeName = employeeAssignments.length > 0 
      ? employeeAssignments[0].user?.name 
      : employeeLeave.length > 0 
        ? employeeLeave[0].user?.name 
        : filterName;
    
    // Calculate date range for current month
    const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    // Count different shift types (total across all branches)
    const dayShifts = employeeAssignments.filter(a => a.shift_type === 'day').length;
    const noonShifts = employeeAssignments.filter(a => a.shift_type === 'noon').length;
    
    // Calculate branch-specific work statistics (include ALL branches)
    const branchStats = {};
    employeeAssignments.forEach(assignment => {
      const branchId = assignment.branch_id;
      const branchName = assignment.branch?.name || `Branch ${branchId}`;
      
      if (!branchStats[branchId]) {
        branchStats[branchId] = {
          branchId,
          branchName,
          dayShifts: 0,
          noonShifts: 0,
          totalShifts: 0,
          workDays: 0
        };
      }
      
      if (assignment.shift_type === 'day') {
        branchStats[branchId].dayShifts++;
      } else if (assignment.shift_type === 'noon') {
        branchStats[branchId].noonShifts++;
      }
      branchStats[branchId].totalShifts++;
    });
    
    // Calculate unique work days per branch
    Object.keys(branchStats).forEach(branchId => {
      const branchAssignments = employeeAssignments.filter(a => a.branch_id === parseInt(branchId));
      const uniqueDates = [...new Set(branchAssignments.map(a => a.assignment_date))];
      branchStats[branchId].workDays = uniqueDates.length;
    });
    
    // Convert branchStats object to array and sort by branch name
    const branchStatsArray = Object.values(branchStats).sort((a, b) => 
      a.branchName.localeCompare(b.branchName)
    );
    
    // Calculate detailed leave type statistics
    const leaveTypeStats = {};
    employeeLeave.forEach(leave => {
      const leaveTypeName = leave.leave_type?.name || 'Unknown';
      if (!leaveTypeStats[leaveTypeName]) {
        leaveTypeStats[leaveTypeName] = {
          name: leaveTypeName,
          count: 0,
          dates: []
        };
      }
      leaveTypeStats[leaveTypeName].count++;
      leaveTypeStats[leaveTypeName].dates.push(leave.leave_date);
    });
    
    // Convert to array and sort by leave type name
    const leaveTypeStatsArray = Object.values(leaveTypeStats).sort((a, b) => 
      a.name.localeCompare(b.name)
    );
    
    // Get simple list of leave types for backward compatibility
    const leaveTypes = [...new Set(employeeLeave.map(leave => leave.leave_type?.name))].filter(Boolean);
    
    return {
      employeeName,
      workDays: workDates.length,
      leaveDays: leaveDates.length,
      // Distinct days engaged (union), so a day with BOTH a work assignment and
      // approved leave is counted once rather than double-counted by summing.
      totalDays: new Set([...workDates, ...leaveDates]).size,
      monthName,
      workDates: workDates.sort(),
      leaveDates: leaveDates.sort(),
      assignments: employeeAssignments,
      leaves: employeeLeave,
      dayShifts,
      noonShifts,
      leaveTypes,
      leaveTypeStats: leaveTypeStatsArray,
      branchStats: branchStatsArray
    };
  };

  const handleDateClick = (date) => {
    if (!hasPermission(['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor'])) return;
    
    // Handle direct assign mode
    if (isDirectAssignMode) {
      handleDirectAssignDateClick(date);
      return;
    }
    
    setSelectedDate(date);
    
    if (viewMode === 'leave') {
      // Show leave management form
      setShowLeaveForm(true);
      setShowForm(false);
    } else {
      // Show work assignment form
      setEditingAssignment(null);
      setShowForm(true);
      setShowLeaveForm(false);
    }
  };

  const handleDirectAssignDateClick = (date) => {
    // Ensure date is within current month
    const clickedDate = new Date(date);
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    if (clickedDate.getMonth() !== currentMonth || clickedDate.getFullYear() !== currentYear) {
      return; // Don't allow selection outside current month
    }
    
    // Use manual date formatting to avoid timezone issues
    const year = clickedDate.getFullYear();
    const month = String(clickedDate.getMonth() + 1).padStart(2, '0');
    const day = String(clickedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Toggle date selection
    setDirectAssignSelectedDates(prev => {
      const isSelected = prev.some(d => {
        let prevDateStr;
        if (typeof d === 'string') {
          prevDateStr = d;
        } else {
          const prevYear = d.getFullYear();
          const prevMonth = String(d.getMonth() + 1).padStart(2, '0');
          const prevDay = String(d.getDate()).padStart(2, '0');
          prevDateStr = `${prevYear}-${prevMonth}-${prevDay}`;
        }
        return prevDateStr === dateStr;
      });
      
      if (isSelected) {
        // Remove date from selection
        return prev.filter(d => {
          let prevDateStr;
          if (typeof d === 'string') {
            prevDateStr = d;
          } else {
            const prevYear = d.getFullYear();
            const prevMonth = String(d.getMonth() + 1).padStart(2, '0');
            const prevDay = String(d.getDate()).padStart(2, '0');
            prevDateStr = `${prevYear}-${prevMonth}-${prevDay}`;
          }
          return prevDateStr !== dateStr;
        });
      } else {
        // Add date to selection
        return [...prev, dateStr];
      }
    });
  };

  const toggleDirectAssignMode = () => {
    setIsDirectAssignMode(prev => !prev);
    // Clear selections when toggling mode
    setDirectAssignSelectedDates([]);
    // Close other forms/modes
    setShowForm(false);
    setShowLeaveForm(false);
    setShowBulkAssignmentForm(false);
    setSelectedDate(null);
  };

  const handleDirectAssignSuccess = async () => {
    // Clear direct assign selections
    setDirectAssignSelectedDates([]);
    // Refresh calendar data
    await fetchBranchAssignments();
  };

  const isDateDirectAssignSelected = (date) => {
    // Use manual date formatting to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    return directAssignSelectedDates.some(d => {
      let selectedDateStr;
      if (typeof d === 'string') {
        selectedDateStr = d;
      } else {
        const selectedYear = d.getFullYear();
        const selectedMonth = String(d.getMonth() + 1).padStart(2, '0');
        const selectedDay = String(d.getDate()).padStart(2, '0');
        selectedDateStr = `${selectedYear}-${selectedMonth}-${selectedDay}`;
      }
      return selectedDateStr === dateStr;
    });
  };

  const handleAssignmentClick = (assignment) => {
    if (viewMode !== 'assignments') return; // Only allow assignment actions in assignments mode
    
    // Show delete confirmation instead of edit form
    setAssignmentToDelete(assignment);
    setShowDeleteConfirm(true);
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditingAssignment(null);
    setSelectedDate(null);
    fetchBranchAssignments();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingAssignment(null);
    setSelectedDate(null);
  };

  const handleLeaveFormSave = () => {
    setShowLeaveForm(false);
    setSelectedDate(null);
    fetchLeaveRequests(); // Refresh leave requests
  };

  const handleLeaveFormCancel = () => {
    setShowLeaveForm(false);
    setSelectedDate(null);
  };

  const handleDeleteAssignment = async () => {
    if (!assignmentToDelete) return;
    
    try {
      setLoading(true);
      await workAssignmentAPI.delete(assignmentToDelete.id);
      
      // Refresh the calendar after deletion
      fetchBranchAssignments();
      
      // Close the confirmation modal
      setShowDeleteConfirm(false);
      setAssignmentToDelete(null);
    } catch (error) {
      console.error('Error deleting assignment:', error);
      alert('Failed to delete assignment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setAssignmentToDelete(null);
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prevDate => {
      const currentYear = prevDate.getFullYear();
      const currentMonth = prevDate.getMonth();
      
      // Calculate new month and year
      const newMonth = currentMonth + direction;
      const newDate = new Date(currentYear, newMonth, 1);
      
      return newDate;
    });
  };

  const calendarDays = getCalendarDays();
  const today = new Date();
  const employeeStats = getEmployeeStatistics();

  return (
    <CalendarContainer>
      {!hideHeader && (
      <CalendarHeader>
        <HeaderContent>
          <div className="header-top">
            <h2>Branch Calendar</h2>
            <BranchSelector
              selectedBranchId={selectedBranchId}
              onBranchChange={setSelectedBranchId}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '5px' }}>
                <Button 
                  onClick={() => setViewMode('assignments')}
                  style={{ 
                    backgroundColor: viewMode === 'assignments' ? '#930c00' : '#efe7d2',
                    color: viewMode === 'assignments' ? '#ffffff' : '#6b645c',
                    fontSize: '12px',
                    padding: '6px 12px'
                  }}
                >
                  Work Assignments
                </Button>
                <Button 
                  onClick={() => setViewMode('leave')}
                  style={{ 
                    backgroundColor: viewMode === 'leave' ? '#930c00' : '#efe7d2',
                    color: viewMode === 'leave' ? '#ffffff' : '#6b645c',
                    fontSize: '12px',
                    padding: '6px 12px'
                  }}
                >
                  Leave Management
                </Button>
                {viewMode === 'assignments' && hasPermission(['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']) && (
                  <>
                    <Button 
                      onClick={() => setShowBulkAssignmentForm(true)}
                      style={{ 
                        backgroundColor: '#930c00',
                        color: 'white',
                        fontSize: '12px',
                        padding: '6px 12px'
                      }}
                    >
                      Bulk Assign
                    </Button>
                    <Button 
                      onClick={toggleDirectAssignMode}
                      style={{ 
                        backgroundColor: isDirectAssignMode ? '#930c00' : '#efe7d2',
                        color: isDirectAssignMode ? '#ffffff' : '#6b645c',
                        fontSize: '12px',
                        padding: '6px 12px'
                      }}
                    >
                      {isDirectAssignMode ? 'Exit Direct' : 'Direct Assign'}
                    </Button>
                  </>
                )}
              </div>
              
              {/* Calendar Filters - Only show in assignments view */}
              {viewMode === 'assignments' && (
                <div style={{ 
                  display: 'flex', 
                  gap: '10px', 
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  borderLeft: '1px solid #e7e3db',
                  paddingLeft: '10px',
                  marginLeft: '10px'
                }}>
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    style={{
                      padding: '6px 8px',
                      fontSize: '12px',
                      border: '1px solid #e7e3db',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      minWidth: '120px'
                    }}
                  >
                    <option value="">All Roles</option>
                    <option value="manager">Manager</option>
                    <option value="makeup">Makeup</option>
                    <option value="photographer">Photographer</option>
                    <option value="retoucher">Retoucher</option>
                  </select>
                  
                  <input
                    type="text"
                    placeholder="Filter by name..."
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    style={{
                      padding: '6px 8px',
                      fontSize: '12px',
                      border: '1px solid #e7e3db',
                      borderRadius: '4px',
                      minWidth: '140px'
                    }}
                  />
                  
                  {(filterRole || filterName) && (
                    <Button
                      onClick={() => {
                        setFilterRole('');
                        setFilterName('');
                      }}
                      style={{
                        backgroundColor: '#efe7d2',
                        color: '#6b645c',
                        fontSize: '11px',
                        padding: '6px 10px'
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              )}
              
              <Button onClick={() => navigateMonth(-1)}>« Prev</Button>
          <span style={{ 
            margin: '0 10px', 
            fontWeight: 'bold',
            fontSize: 'clamp(14px, 2.5vw, 18px)',
            textAlign: 'center',
            minWidth: '150px'
          }}>
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <Button onClick={() => navigateMonth(1)}>Next »</Button>
        </div>
      </div>
      {viewMode === 'assignments' && (
        <RoleLegend>
          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#666' }}>Roles:</span>
          <LegendItem>
            <span className="badge" style={{ backgroundColor: '#e91e63' }}></span>
            Manager
          </LegendItem>
          <LegendItem>
            <span className="badge" style={{ backgroundColor: '#9c27b0' }}></span>
            Makeup
          </LegendItem>
          <LegendItem>
            <span className="badge" style={{ backgroundColor: '#2196f3' }}></span>
            Photographer
          </LegendItem>
          <LegendItem>
            <span className="badge" style={{ backgroundColor: '#4caf50' }}></span>
            Retoucher
          </LegendItem>
          <LegendItem>
            <span className="badge" style={{ backgroundColor: '#930c00' }}></span>
            Other Branch (Cross-Branch)
          </LegendItem>
        </RoleLegend>
      )}
    </HeaderContent>
  </CalendarHeader>
      )}

      {/* Staffing Requirements Banner - Only show in assignments view */}
      {viewMode === 'assignments' && (
        <StaffingRequirements>
          <div className="requirements-title">
            ⚠️ Shift Staffing Requirements
          </div>
          <div className="requirements-text">
            Each shift (Day & Noon) must have at least <strong>1 Makeup Artist</strong> and <strong>1 Photographer</strong>
            <div className="legend">
              <div className="legend-item">
                <div className="color-box" style={{ backgroundColor: '#e7c9c3' }}></div>
                Insufficient Staff
              </div>
              <div className="legend-item">
                <div className="color-box" style={{ backgroundColor: '#cfe3d7' }}></div>
                Adequate Staff
              </div>
            </div>
          </div>
        </StaffingRequirements>
      )}

      {/* Employee Summary - Only show when filtering by name */}
      {viewMode === 'assignments' && filterName && employeeStats && (
        <EmployeeSummary>
          <div className="summary-header">
            <span>👤</span>
            Employee Summary: {employeeStats.employeeName}
          </div>
          
          <div className="summary-grid">
            <div className="summary-card work-days">
              <div className="card-icon"><FiBriefcase /></div>
              <div className="card-title">Work Days</div>
              <div className="card-value">{employeeStats.workDays}</div>
              <div className="card-detail">
                {employeeStats.workDays > 0 ? (
                  <div className="branch-breakdown">
                    <div className="total-line">
                      Total: {employeeStats.workDays} {employeeStats.workDays === 1 ? 'day' : 'days'} (Day shifts: {employeeStats.dayShifts}, Noon shifts: {employeeStats.noonShifts})
                    </div>
                    {employeeStats.branchStats.map((branch, index) => (
                      <div key={index} className="branch-line">
                        <span className="branch-name">{branch.branchName}:</span> {branch.workDays} {branch.workDays === 1 ? 'day' : 'days'} (Day: {branch.dayShifts}, Noon: {branch.noonShifts})
                      </div>
                    ))}
                  </div>
                ) : (
                  "No work assignments"
                )}
              </div>
            </div>
            
            <div className="summary-card leave-days">
              <div className="card-icon"><FiShield /></div>
              <div className="card-title">Leave Days</div>
              <div className="card-value">{employeeStats.leaveDays}</div>
              <div className="card-detail">
                {employeeStats.leaveTypeStats.length > 0 ? (
                  <div className="leave-breakdown">
                    <div className="total-line">
                      Total leave days: {employeeStats.leaveDays}
                    </div>
                    {employeeStats.leaveTypeStats.map((leaveType, index) => (
                      <div key={index} className="leave-type-line">
                        <span className="leave-type-name">{leaveType.name}:</span> {leaveType.count} {leaveType.count === 1 ? 'day' : 'days'}
                      </div>
                    ))}
                  </div>
                ) : (
                  "No approved leave"
                )}
              </div>
            </div>
            
            <div className="summary-card total-days">
              <div className="card-icon"><FiCalendar /></div>
              <div className="card-title">Total Days</div>
              <div className="card-value">{employeeStats.totalDays}</div>
              <div className="card-detail">
                Combined work and leave days in {employeeStats.monthName}
              </div>
            </div>
          </div>
          
          <div className="date-range">
            <FiActivity style={{ marginRight: '8px' }} /> Summary for {employeeStats.monthName}
            {employeeStats.workDays > 0 && ` • Work: ${employeeStats.workDays} days`}
            {employeeStats.leaveDays > 0 && ` • Leave: ${employeeStats.leaveDays} days`}
            {employeeStats.totalDays === 0 && " • No assignments or leave found for this employee"}
          </div>
        </EmployeeSummary>
      )}

      {loading && <div>Loading calendar...</div>}

      {/* Direct Assignment Form */}
      {viewMode === 'assignments' && hasPermission(['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']) && (
        <DirectAssignForm
          isActive={isDirectAssignMode}
          onToggle={toggleDirectAssignMode}
          selectedBranchId={selectedBranchId}
          currentDate={currentDate}
          selectedDates={directAssignSelectedDates}
          onDateSelectionChange={setDirectAssignSelectedDates}
          onAssignmentSuccess={handleDirectAssignSuccess}
          existingAssignments={assignments}
          holidays={holidays}
          leaveRequests={leaveRequests}
        />
      )}

      <CalendarGrid>
        {calendarDays.map((date, index) => {
          const dayAssignments = getAssignmentsForDate(date);
          const dayShifts = dayAssignments.filter(a => a.shift_type === 'day');
          const noonShifts = dayAssignments.filter(a => a.shift_type === 'noon');
          const dayHolidays = getHolidaysForDate(date);
          const dayLeave = getLeaveForDate(date);
          
          const isToday = date.toDateString() === today.toDateString();
          
          // Check staffing requirements for this date (only in assignments view)
          const staffingStatus = viewMode === 'assignments' ? checkStaffingRequirements(dayAssignments) : null;
          
          // Get role counts by shift for date summary
          const { dayShiftCounts, noonShiftCounts } = getRoleCountsByShift(dayAssignments);
          
          // Define simplified role categories to display
          const roleCategories = ['manager', 'makeup', 'photographer', 'retoucher'];
          const categoriesToShow = filterRole ? [filterRole] : roleCategories;
          
          // Group assignments by category for both shifts
          const groupByCategory = (assignments) => {
            const categoryGroups = {};
            // Initialize categories with empty arrays
            categoriesToShow.forEach(category => {
              categoryGroups[category] = [];
            });
            // Add assignments to their respective categories
            assignments.forEach(assignment => {
              const role = assignment.role || 'unknown';
              // Map role to category
              let category = null;
              if (role === 'store_manager' || role === 'manager_assistant') category = 'manager';
              else if (role === 'makeup' || role === 'makeup_supervisor') category = 'makeup';
              else if (role === 'photographer' || role === 'photographer_supervisor') category = 'photographer';
              else if (role === 'retoucher' || role === 'retoucher_supervisor') category = 'retoucher';
              
              if (category && categoryGroups[category]) {
                categoryGroups[category].push(assignment);
              }
            });
            return categoryGroups;
          };
          
          const dayRoles = groupByCategory(dayShifts);
          const noonRoles = groupByCategory(noonShifts);
          
          return (
            <CalendarDay
              key={index}
              className={`${isToday ? 'today' : ''}`}
              staffingStatus={staffingStatus}
              isDirectAssignSelected={isDirectAssignMode && isDateDirectAssignSelected(date)}
              onClick={() => handleDateClick(date)}
            >
              <DayHeader>
                <div className="date-info">
                  <DayNumber>{date.getDate()}</DayNumber>
                  <DayOfWeek>{date.toLocaleDateString('en-US', { weekday: 'short' })}</DayOfWeek>
                </div>
                {viewMode === 'assignments' && (
                  <div className="role-summary">
                    {/* Day Shift Summary */}
                    <div className="shift-summary">
                      <span className="shift-label">Day:</span>
                      <div className={`role-count manager ${dayShiftCounts.manager === 0 ? 'zero' : ''}`} title="Managers - Day Shift">
                        M:{dayShiftCounts.manager}
                      </div>
                      <div className={`role-count makeup ${dayShiftCounts.makeup === 0 ? 'zero' : ''}`} title="Makeup Artists - Day Shift">
                        MU:{dayShiftCounts.makeup}
                      </div>
                      <div className={`role-count photographer ${dayShiftCounts.photographer === 0 ? 'zero' : ''}`} title="Photographers - Day Shift">
                        P:{dayShiftCounts.photographer}
                      </div>
                      <div className={`role-count retoucher ${dayShiftCounts.retoucher === 0 ? 'zero' : ''}`} title="Retouchers - Day Shift">
                        R:{dayShiftCounts.retoucher}
                      </div>
                    </div>
                    {/* Noon Shift Summary */}
                    <div className="shift-summary">
                      <span className="shift-label">Noon:</span>
                      <div className={`role-count manager ${noonShiftCounts.manager === 0 ? 'zero' : ''}`} title="Managers - Noon Shift">
                        M:{noonShiftCounts.manager}
                      </div>
                      <div className={`role-count makeup ${noonShiftCounts.makeup === 0 ? 'zero' : ''}`} title="Makeup Artists - Noon Shift">
                        MU:{noonShiftCounts.makeup}
                      </div>
                      <div className={`role-count photographer ${noonShiftCounts.photographer === 0 ? 'zero' : ''}`} title="Photographers - Noon Shift">
                        P:{noonShiftCounts.photographer}
                      </div>
                      <div className={`role-count retoucher ${noonShiftCounts.retoucher === 0 ? 'zero' : ''}`} title="Retouchers - Noon Shift">
                        R:{noonShiftCounts.retoucher}
                      </div>
                    </div>
                  </div>
                )}
              </DayHeader>
              
              {/* Display holidays */}
              {dayHolidays.map(holiday => (
                <Holiday
                  key={holiday.id}
                  group={holiday.holiday_group?.name || 'Unknown'}
                  title={`Holiday Group ${holiday.holiday_group?.name || 'Unknown'}: ${holiday.name}`}
                >
                  🎉 {holiday.name} [{holiday.holiday_group?.name}]
                </Holiday>
              ))}
              
              {/* Display Leave - Only visible in Leave Management view */}
              {viewMode === 'leave' && dayLeave.length > 0 && (
                <div style={{ marginTop: '4px' }}>
                  {dayLeave.map(leave => (
                    <LeaveItem
                      key={leave.id}
                      leaveType={leave.leave_type?.name}
                      title={`${leave.user?.name}: ${leave.leave_type?.name} - ${leave.reason || 'No reason'}`}
                    >
                      <div style={{ fontWeight: 'bold', fontSize: '11px' }}>
                        <FiShield style={{ marginRight: '4px' }} /> {leave.user?.name}
                      </div>
                      <div style={{ fontSize: '9px', opacity: 0.8 }}>
                        {leave.leave_type?.name}
                      </div>
                    </LeaveItem>
                  ))}
                </div>
              )}
              
              {/* Leave Management View - Additional Controls */}
              {viewMode === 'leave' && (
                <>
                  {/* Leave management specific content can go here if needed */}
                </>
              )}
              
              {/* Work Assignments View */}
              {viewMode === 'assignments' && (
                <>
                  {/* Day Shift - Always show */}
              <ShiftSection>
                <ShiftLabel>Day Shift</ShiftLabel>
                {categoriesToShow.map(category => (
                  <div key={`day-${category}`}>
                    {dayRoles[category].length > 0 ? (
                      dayRoles[category].map(assignment => {
                        return (
                          <Assignment
                            key={assignment.id}
                            shift="day"
                            role={assignment.role}
                            isCrossBranch={assignment.isCrossBranch}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!assignment.isCrossBranch) {
                                handleAssignmentClick(assignment);
                              }
                            }}
                          >
                            <div className="employee-name">
                              {formatEmployeeDisplayName(assignment)}
                              {assignment.isCrossBranch && assignment.branch && (
                                <span style={{ 
                                  fontSize: '0.65rem', 
                                  fontWeight: 400, 
                                  color: '#930c00',
                                  marginLeft: '4px',
                                  opacity: 0.9
                                }}>
                                  @ {assignment.branch.name}
                                </span>
                              )}
                            </div>
                            <div className="employee-role">
                              <span>
                                <span className="role-badge">{assignment.role}</span>
                                {/* <span className="shift-badge">DAY</span> */}
                              </span>
                            </div>
                          </Assignment>
                        );
                      })
                    ) : (
                      // Only show "No assignment" if no specific filters are applied
                      !filterRole && !filterName && (
                        <div style={{ 
                          fontSize: '0.7rem', 
                          color: '#999', 
                          fontStyle: 'italic', 
                          marginLeft: '8px',
                          padding: '4px',
                          opacity: 0.7
                        }}>
                          {category.charAt(0).toUpperCase() + category.slice(1)}: No assignment
                        </div>
                      )
                    )}
                  </div>
                ))}
              </ShiftSection>
              
              {/* Noon Shift - Always show */}
              <ShiftSection>
                <ShiftLabel>Noon Shift</ShiftLabel>
                {categoriesToShow.map(category => (
                  <div key={`noon-${category}`}>
                    {noonRoles[category].length > 0 ? (
                      noonRoles[category].map(assignment => {
                        return (
                          <Assignment
                            key={assignment.id}
                            shift="noon"
                            role={assignment.role}
                            isCrossBranch={assignment.isCrossBranch}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!assignment.isCrossBranch) {
                                handleAssignmentClick(assignment);
                              }
                            }}
                          >
                            <div className="employee-name">
                              {formatEmployeeDisplayName(assignment)}
                              {assignment.isCrossBranch && assignment.branch && (
                                <span style={{ 
                                  fontSize: '0.65rem', 
                                  fontWeight: 400, 
                                  color: '#930c00',
                                  marginLeft: '4px',
                                  opacity: 0.9
                                }}>
                                  @ {assignment.branch.name}
                                </span>
                              )}
                            </div>
                            <div className="employee-role">
                              <span>
                                <span className="role-badge">{assignment.role}</span>
                                {/* <span className="shift-badge">NOON</span> */}
                              </span>
                            </div>
                          </Assignment>
                        );
                      })
                    ) : (
                      // Only show "No assignment" if no specific filters are applied
                      !filterRole && !filterName && (
                        <div style={{ 
                          fontSize: '0.7rem', 
                          color: '#999', 
                          fontStyle: 'italic', 
                          marginLeft: '8px',
                          padding: '4px',
                          opacity: 0.7
                        }}>
                          {category.charAt(0).toUpperCase() + category.slice(1)}: No assignment
                        </div>
                      )
                    )}
                  </div>
                ))}
              </ShiftSection>
                </>
              )}
            </CalendarDay>
          );
        })}
      </CalendarGrid>

      {/* Assignment Form Modal */}
      {showForm && (
        <Modal>
          <ModalContent>
            <WorkAssignmentForm
              assignment={editingAssignment}
              defaultDate={selectedDate}
              defaultBranchId={selectedBranchId}
              onSave={handleFormSave}
              onCancel={handleFormCancel}
            />
          </ModalContent>
        </Modal>
      )}

      {/* Leave Management Form Modal */}
      {showLeaveForm && (
        <Modal>
          <ModalContent>
            <LeaveManagementForm
              selectedDate={selectedDate}
              onSave={handleLeaveFormSave}
              onCancel={handleLeaveFormCancel}
            />
          </ModalContent>
        </Modal>
      )}

      {/* Delete Assignment Confirmation Modal */}
      {showDeleteConfirm && assignmentToDelete && (
        <Modal>
          <ModalContent>
            <DeleteConfirmation>
              <h3><FiTrash style={{ marginRight: '8px' }} />Delete Assignment</h3>
              <p>Are you sure you want to delete this work assignment?</p>
              
              <div className="assignment-info">
                <div className="employee-name">
                  {formatEmployeeDisplayName(assignmentToDelete)}
                </div>
                <div className="assignment-details">
                  <strong>Role:</strong> {assignmentToDelete.role || 'Unknown'}<br/>
                  <strong>Shift:</strong> {assignmentToDelete.shift_type === 'day' ? 'Day Shift' : 'Noon Shift'}<br/>
                  <strong>Date:</strong> {new Date(assignmentToDelete.assignment_date).toLocaleDateString()}
                </div>
              </div>
              
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                This action cannot be undone.
              </p>
              
              <div className="button-group">
                <Button 
                  className="secondary" 
                  onClick={handleDeleteCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button 
                  className="danger" 
                  onClick={handleDeleteAssignment}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete Assignment'}
                </Button>
              </div>
            </DeleteConfirmation>
          </ModalContent>
        </Modal>
      )}

      {/* Bulk Assignment Form */}
      <BulkAssignmentForm
        isOpen={showBulkAssignmentForm}
        onClose={() => setShowBulkAssignmentForm(false)}
        defaultBranchId={selectedBranchId}
        currentDate={currentDate}
        onSuccess={async () => {
          try {
            // Small delay to ensure assignments are persisted
            await new Promise(resolve => setTimeout(resolve, 500));
            await fetchBranchAssignments();
            setShowBulkAssignmentForm(false);
          } catch (error) {
            console.error('Error in bulk assignment success callback:', error);
            // Even if refresh fails, close the modal since assignments were created
            setShowBulkAssignmentForm(false);
          }
        }}
      />
    </CalendarContainer>
  );
};

export default BranchCalendar;